import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { getDomainMarketOrigin } from '@libs/search-service-smart-search';
import { getUrlMetadata } from '@libs/feature-toggle';
import { getDomain } from '@libs/utils';
import type { TripOverviewInitiatedMetadata } from '@libs/google-analytics-events';
import { IngredientType } from '@libs/trip-planner-interfaces';

import type {
  ActivityDetailsTicketSelectedMetadata,
  ActivitySelectionListCardClickedMetaData,
  ActivitySelectionMapEventMetadata,
  BaseMetadata,
  BookIntentSource,
  ChangeHotelEventMetadata,
  CoreEventMetadata,
  EditTripModalOpenedLocationType,
  ExternalErrorQueryParamValue,
  HotelDetailsEventMetadata,
  HotelPeekPriceModalOpenedMetadata,
  HotelSelectionFilterAppliedMetadata,
  HotelSelectionFilterClosedMetadata,
  HotelSelectionFilterOpenedMetadata,
  HotelSelectionFilterRemovedMetadata,
  HotelSelectionFilterResetMetadata,
  HotelSelectionInitiatedMetadata,
  HotelSelectionListCardClickedMetaData,
  HotelSelectionMapEventMetadata,
  HotelSelectionSuggestionTypeSelectedMetadata,
  HotelSelectionToggleViewMetadata,
  HotelSelectionUpsellBannerDisplayedMetadata,
  HotelSelectionUpsellBannerToggledMetadata,
  MetaHotelListEmptyMetadata,
  MissingCarrierLogoMetadata,
  RecipeIngredientsMetadata,
  StatsigUIInteractionsState,
  ToggleMapTypeMetadata,
  TripOverviewAlternativeTicketsDisplayedMetadata,
  TripOverviewBookClickedMetadata,
  TripOverviewBookClickedWithFarAirportMetadata,
  TripOverviewConfigurationPanelMetadata,
  TripOverviewHeaderActionsMetadata,
  TripOverviewMobileControlCenterToggledMetadata,
  TripOverviewPrintModeMetadata,
  TripOverviewFerryTicketUpsellSamePriceHiddenMetadata,
  TripOverviewTransportTicketTypeSelectedMetadata,
  TripOverviewUnavailableProductActionClickedMetadata,
  TripOverviewUnavailableProductRenderedMetadata,
  TripOverviewWarningModalMetadata,
} from '../../models';
import { StatsigEventType } from '../../models';
import { useBrowserStore } from '../browserStore';
import { usePreferredTimeStore } from '../preferredTimeStore';
import { useMetaStore } from '../meta/metaStore';
import type { MissingItineraryStaticInfoError } from '../trip/tripStore.model';
import {
  MissingActivitiesStaticInfoError,
  MissingHotelStaticInfoError,
  MissingItineraryAvailabilityError,
  MissingOwnStayStaticInfoError,
  MissingOwnTransportStaticInfoError,
  MissingTransportStaticInfoError,
} from '../trip/tripStore.model';
import { useQueryParamsStore } from '../queryParamsStore';

import { logStatsigEventWrapper } from './logEventFn';

export interface CheckoutErrorMetadata {
  source: BookIntentSource;
  errorType: string;
  errorMessage: string;
  url: string;
  totalIngredients: number;
  totalAccomodation: number;
  totalFlight: number;
  totalFerry: number;
  totalActivity: number;
  itineraryId: string | undefined;
}

export const useStatsigEventsStore = defineStore('StatsigEvents', () => {
  const browserStore = useBrowserStore();
  const preferredTimeStore = usePreferredTimeStore();
  const metaStore = useMetaStore();
  const queryParamsStore = useQueryParamsStore();

  const baseMetadata = computed<BaseMetadata>(() => {
    const language = getDomain.getLanguageLocale();
    const domain = getDomainMarketOrigin();
    const dateMode = preferredTimeStore.preferredDay
      ? 'selectedDate'
      : 'flexible';
    const isDesktop = browserStore.isSmallerThan1200px ? 'false' : 'true';
    const affiliateId = metaStore.metaAffiliateId ?? '';
    const publishedTripId = queryParamsStore.queryParams?.publishedTripId ?? '';
    return {
      language,
      domain,
      dateMode,
      isDesktop,
      affiliateId,
      publishedTripId,
    };
  });

  // State
  const uiInteractionsState = ref<StatsigUIInteractionsState>({
    shareTripOverview: false,
    travellingWithSemboOverview: false,
    tripSummaryOverview: false,
    peekPriceCalendarHotelSelection: false,
  });

  // Actions: Core
  function logCoreDateChanged(metadata: CoreEventMetadata): void {
    logEvent(StatsigEventType.CoreDateChanged, undefined, { ...metadata });
  }

  function logCorePaxModalOpened(metadata: CoreEventMetadata): void {
    logEvent(StatsigEventType.CorePaxModalOpened, undefined, { ...metadata });
  }

  function logCorePaxUpdated(
    hasSelectedRoom: boolean,
    metadata: CoreEventMetadata
  ): void {
    logEvent(StatsigEventType.CorePaxUpdated, hasSelectedRoom.toString(), {
      ...metadata,
    });
  }

  function logNoAvailForMonth(isFirstSeach: boolean): void {
    logEvent(StatsigEventType.NoAvailForMonth, isFirstSeach.toString());
  }

  function logCoreNightsSelectorOpened(metadata: CoreEventMetadata): void {
    logEvent(StatsigEventType.CoreNightsSelectorOpened, undefined, {
      ...metadata,
    });
  }

  function logCoreDateSelectedAtLeastOneYearAhead(
    metadata: RecipeIngredientsMetadata
  ): void {
    logEvent(StatsigEventType.CoreDateSelectedAtLeastOneYearAhead, undefined, {
      totalIngredients: metadata.totalIngredients.toString(),
      totalAccomodation: metadata.totalAccomodation.toString(),
      totalOwnStay: metadata.totalOwnStay.toString(),
      totalFlight: metadata.totalFlight.toString(),
      totalFerry: metadata.totalFerry.toString(),
      totalUnbookableTransport: metadata.totalUnbookableTransport.toString(),
      totalActivity: metadata.totalActivity.toString(),
    });
  }

  // Actions: Build your trip
  function logBuildYourTripInitiated(): void {
    logEvent(StatsigEventType.BuildYourTripInitiated);
  }

  function logCoreTripAutoSavedAttempt(source: BookIntentSource): void {
    logEvent(StatsigEventType.CoreTripAutoSavedAttempt, undefined, {
      source,
    });
  }

  // Actions: NOTE: Helper events for experiments, remove when not needed anymore
  function logTripOverviewMobileMainSummaryActionClicked(
    clickSource: 'book' | 'summary'
  ): void {
    logEvent(
      StatsigEventType.TripOverviewMobileMainSummaryActionClicked,
      undefined,
      {
        clickSource,
      }
    );
  }

  // Actions: Trip overview
  function logTripOverviewInitiated(
    metadata: TripOverviewInitiatedMetadata
  ): void {
    logEvent(StatsigEventType.TripOverviewInitiated, undefined, {
      totalAccomodation: metadata.totalAccomodation.toString(),
      totalFlight: metadata.totalFlight.toString(),
      totalFerry: metadata.totalFerry.toString(),
      totalActivity: metadata.totalActivity.toString(),
    });
  }

  function logTripOverviewMobileControlCenterToggled(
    metadata: TripOverviewMobileControlCenterToggledMetadata
  ): void {
    logEvent(
      StatsigEventType.TripOverviewMobileControlCenterToggled,
      undefined,
      metadata
    );
  }

  function logTripOverviewAddDestinationClicked(): void {
    logEvent(StatsigEventType.TripOverviewAddDestinationClicked);
  }

  function logTripOverviewBookClicked(
    source: BookIntentSource,
    metadata: TripOverviewBookClickedMetadata
  ): void {
    logEvent(StatsigEventType.TripOverviewBookClicked, undefined, {
      source,
      totalIngredients: metadata.totalIngredients.toString(),
      totalAccomodation: metadata.totalAccomodation.toString(),
      totalOwnStay: metadata.totalOwnStay.toString(),
      totalFlight: metadata.totalFlight.toString(),
      totalFerry: metadata.totalFerry.toString(),
      totalActivity: metadata.totalActivity.toString(),
      totalAmount: metadata.totalAmount.toString(),
      totalUnbookableTransport: metadata.totalUnbookableTransport.toString(),
      market: metadata.market,
      priceType: metadata.priceType,
      totalFlightFlexiUpsells: metadata.totalFlightFlexiUpsells.toString(),
      totalFerryFlexiUpsells: metadata.totalFerryFlexiUpsells.toString(),
      uiInteractionsShareTripOverview:
        uiInteractionsState.value.shareTripOverview.toString(),
      uiInteractionsTravellingWithSemboOverview:
        uiInteractionsState.value.travellingWithSemboOverview.toString(),
      uiInteractionsTripSummaryOverview:
        uiInteractionsState.value.tripSummaryOverview.toString(),
      uiInteractionsPeekPriceCalendarHotelSelection:
        uiInteractionsState.value.peekPriceCalendarHotelSelection.toString(),
    });
  }

  function logTripOverviewCheckoutRedirect(source: BookIntentSource): void {
    logEvent(StatsigEventType.TripOverviewCheckoutRedirect, undefined, {
      source,
    });
  }

  function logOverviewCheckoutError(
    eventType:
      | StatsigEventType.OverviewCheckoutBasketError
      | StatsigEventType.OverviewCheckoutTokenError
      | StatsigEventType.OverviewCheckoutUnknownError,
    metadata: CheckoutErrorMetadata
  ): void {
    logEvent(eventType, undefined, {
      source: metadata.source,
      errorType: metadata.errorType,
      errorMessage: metadata.errorMessage,
      url: metadata.url,
      totalIngredients: metadata.totalIngredients.toString(),
      totalAccomodation: metadata.totalAccomodation.toString(),
      totalFlight: metadata.totalFlight.toString(),
      totalFerry: metadata.totalFerry.toString(),
      totalActivity: metadata.totalActivity.toString(),
      itineraryId: metadata.itineraryId ?? '',
    });
  }

  function logOverviewCheckoutBasketError(
    metadata: CheckoutErrorMetadata
  ): void {
    logOverviewCheckoutError(
      StatsigEventType.OverviewCheckoutBasketError,
      metadata
    );
  }

  function logOverviewCheckoutTokenError(
    metadata: CheckoutErrorMetadata
  ): void {
    logOverviewCheckoutError(
      StatsigEventType.OverviewCheckoutTokenError,
      metadata
    );
  }

  function logOverviewCheckoutUnknownError(
    metadata: CheckoutErrorMetadata
  ): void {
    logOverviewCheckoutError(
      StatsigEventType.OverviewCheckoutUnknownError,
      metadata
    );
  }

  function logTripOverviewBookClickedWithFarAirport(
    metadata: TripOverviewBookClickedWithFarAirportMetadata
  ): void {
    logEvent(
      StatsigEventType.TripOverviewBookClickedWithFarAirport,
      undefined,
      {
        distanceKm: metadata.distanceKm.toString(),
        hotelPolygonId: metadata.hotelPolygonId?.toString() ?? '',
        airportIata: metadata.airportIata,
      }
    );
  }

  function serializeWarningModalMetadata(
    metadata: TripOverviewWarningModalMetadata
  ): Record<string, string> {
    return {
      warnings: metadata.warnings.join(','),
      warningCount: metadata.warningCount.toString(),
    };
  }

  function logTripOverviewWarningModalDisplayed(
    metadata: TripOverviewWarningModalMetadata
  ): void {
    logEvent(
      StatsigEventType.TripOverviewWarningModalDisplayed,
      undefined,
      serializeWarningModalMetadata(metadata)
    );
  }

  function logTripOverviewWarningModalClosed(
    metadata: TripOverviewWarningModalMetadata
  ): void {
    logEvent(
      StatsigEventType.TripOverviewWarningModalClosed,
      undefined,
      serializeWarningModalMetadata(metadata)
    );
  }

  function logTripOverviewWarningModalContinued(
    metadata: TripOverviewWarningModalMetadata
  ): void {
    logEvent(
      StatsigEventType.TripOverviewWarningModalContinued,
      undefined,
      serializeWarningModalMetadata(metadata)
    );
  }

  function logTripOverviewHotelChangeClicked(
    metadata: ChangeHotelEventMetadata
  ): void {
    logEvent(StatsigEventType.TripOverviewHotelChangeClicked, undefined, {
      ...metadata,
    });
  }

  function logTripOverviewTransportSelectorOpened(): void {
    logEvent(StatsigEventType.TripOverviewTransportSelectorOpened);
  }

  function logTripOverviewAccommodationSelectorOpened(): void {
    logEvent(StatsigEventType.TripOverviewAccommodationSelectorOpened);
  }

  function logTripOverviewExploreActivitiesClicked(): void {
    logEvent(StatsigEventType.TripOverviewExploreActivitiesClicked);
  }

  function logTripOverviewActivityBannerElementClicked(): void {
    logEvent(StatsigEventType.TripOverviewActivityBannerElementClicked);
  }

  function logTripOverviewAlternativeAccommodationGalleryImageSwipe(): void {
    logEvent(
      StatsigEventType.TripOverviewAlternativeAccommodationGalleryImageSwipe
    );
  }

  function logTripOverviewActivityGalleryImageSwipe(): void {
    logEvent(StatsigEventType.TripOverviewActivityGalleryImageSwipe);
  }

  function logTripOverviewAlternativeActivityGalleryImageSwipe(): void {
    logEvent(StatsigEventType.TripOverviewAlternativeActivityGalleryImageSwipe);
  }

  function logTripOverviewHotelToggle(value: boolean): void {
    logEvent(StatsigEventType.TripOverviewHotelToggle, value.toString());
  }

  function logTripOverviewEditTripModalOpened(
    buttonLocation: EditTripModalOpenedLocationType
  ): void {
    logEvent(StatsigEventType.TripOverviewEditTripModalOpened, buttonLocation);
  }

  function logTripOverviewEditTripModalDestinationAdded(): void {
    logEvent(StatsigEventType.TripOverviewEditTripModalDestinationAdded);
  }

  function logTripOverviewEditTripModalDestinationChanged(): void {
    logEvent(StatsigEventType.TripOverviewEditTripModalDestinationChanged);
  }

  function logTripOverviewEditTripModalDestinationRearranged(): void {
    logEvent(StatsigEventType.TripOverviewEditTripModalDestinationRearranged);
  }

  function logTripOverviewFlyFromButtonClicked(): void {
    logEvent(StatsigEventType.TripOverviewFlyFromButtonClicked);
  }

  function logTripOverviewUnavailableProductActionClicked(
    metadata: TripOverviewUnavailableProductActionClickedMetadata
  ): void {
    logEvent(
      StatsigEventType.TripOverviewUnavailableProductActionClicked,
      undefined,
      metadata
    );
  }

  function logTripOverviewUnavailableProductRendered(
    metadata: TripOverviewUnavailableProductRenderedMetadata
  ): void {
    const serializedMetadata: Record<string, string> =
      metadata.productType === 'accommodation'
        ? {
            ...metadata,
            activeFilters: JSON.stringify(metadata.activeFilters),
            affiliateId: metaStore.metaAffiliateId ?? 'noAffiliateId',
            url: window.location.href,
          }
        : {
            ...metadata,
            affiliateId: metaStore.metaAffiliateId ?? 'noAffiliateId',
            url: window.location.href,
          };
    logEvent(
      StatsigEventType.TripOverviewUnavailableProductRendered,
      undefined,
      serializedMetadata
    );
  }

  function logTripOverviewSeeAlternativeRoomsWhenNoAvail(): void {
    logEvent(StatsigEventType.TripOverviewSeeAlternativeRoomsWhenNoAvail);
  }

  function logTripOverviewChangeDateClicked(): void {
    logEvent(StatsigEventType.TripOverviewChangeDateClicked);
  }

  function logTripOverviewConfirmAlternativeRoomSuggestionWhenNoAvail(): void {
    logEvent(
      StatsigEventType.TripOverviewConfirmAlternativeRoomSuggestionWhenNoAvail
    );
  }

  function logTripOverviewSeeAlternativeTicketsWhenNoAvail(): void {
    logEvent(StatsigEventType.TripOverviewSeeAlternativeTicketsWhenNoAvail);
  }

  function logTripOverviewAlternativeStaySelected(): void {
    logEvent(StatsigEventType.TripOverviewAlternativeStaySelected);
  }

  function logTripOverviewAlternativeActivitySelected(): void {
    logEvent(StatsigEventType.TripOverviewAlternativeActivitySelected);
  }

  function logTripOverviewAlternativeRoomsDisplayed(): void {
    logEvent(StatsigEventType.TripOverviewAlternativeRoomsDisplayed);
  }

  function logTripOverviewAlternativeTicketsDisplayed(
    metadata: TripOverviewAlternativeTicketsDisplayedMetadata
  ): void {
    logEvent(
      StatsigEventType.TripOverviewAlternativeTicketsDisplayed,
      undefined,
      {
        activityCode: metadata.activityCode,
        activityName: metadata.activityName,
        date: metadata.date,
        destination: metadata.destination,
      }
    );
  }

  function logTripOverviewAlternativeFlightDisplayed(): void {
    logEvent(StatsigEventType.TripOverviewAlternativeFlightDisplayed);
  }

  function logTripOverviewAlternativeFlightSelected(): void {
    logEvent(StatsigEventType.TripOverviewAlternativeFlightSelected);
  }

  function logTripOverviewAlternativeFerryDisplayed(): void {
    logEvent(StatsigEventType.TripOverviewAlternativeFerryDisplayed);
  }

  function logTripOverviewAlternativeFerrySelected(): void {
    logEvent(StatsigEventType.TripOverviewAlternativeFerrySelected);
  }

  function logTripOverviewHeaderActionClicked(
    metadata: TripOverviewHeaderActionsMetadata
  ): void {
    logEvent(
      StatsigEventType.TripOverviewHeaderActionClicked,
      undefined,
      metadata
    );
  }

  function logTripOverviewContinueWithSelectedDateModalOpened(): void {
    logEvent(StatsigEventType.TripOverviewContinueWithSelectedDateModalOpened);
  }

  function logTripOverviewEditRoomClicked(): void {
    logEvent(StatsigEventType.TripOverviewEditRoomClicked);
  }

  function logTripOverviewActivityRemoved(): void {
    logEvent(StatsigEventType.TripOverviewActivityRemoved);
  }

  function logTripOverviewFlightAdded(): void {
    logEvent(StatsigEventType.TripOverviewFlightAdded);
  }

  function logTripOverviewFlightRemoved(): void {
    logEvent(StatsigEventType.TripOverviewFlightRemoved);
  }

  function logTripOverviewFerryAdded(): void {
    logEvent(StatsigEventType.TripOverviewFerryAdded);
  }

  function logTripOverviewFerryRemoved(): void {
    logEvent(StatsigEventType.TripOverviewFerryRemoved);
  }

  function logTripOverviewTransportTicketTypeSelected(
    metadata: TripOverviewTransportTicketTypeSelectedMetadata
  ): void {
    logEvent(
      StatsigEventType.TripOverviewTransportTicketTypeSelected,
      undefined,
      {
        ingredientType: metadata.ingredientType,
        ticketType: metadata.ticketType,
      }
    );
  }

  function logTripOverviewFerryTicketUpsellSamePriceHidden(
    metadata: TripOverviewFerryTicketUpsellSamePriceHiddenMetadata
  ): void {
    logEvent(
      StatsigEventType.TripOverviewFerryTicketUpsellSamePriceHidden,
      undefined,
      {
        hiddenCount: metadata.hiddenCount.toString(),
        totalCount: metadata.totalCount.toString(),
      }
    );
  }

  function logTripOverviewFlightChangeClicked(): void {
    logEvent(StatsigEventType.TripOverviewFlightChangeClicked);
  }

  function logTripOverviewFerryChangeClicked(): void {
    logEvent(StatsigEventType.TripOverviewFerryChangeClicked);
  }

  function logTripOverviewActivityChangeClicked(): void {
    logEvent(StatsigEventType.TripOverviewActivityChangeClicked);
  }

  function logTripOverviewFlightShowDetailsClicked(): void {
    logEvent(StatsigEventType.TripOverviewFlightShowDetailsClicked);
  }

  function logTripOverviewFerryShowDetailsClicked(): void {
    logEvent(StatsigEventType.TripOverviewFerryShowDetailsClicked);
  }

  function logTripOverviewHotelShowDetailsClicked(): void {
    logEvent(StatsigEventType.TripOverviewHotelShowDetailsClicked);
  }

  function logTripOverviewActivityShowDetailsClicked(): void {
    logEvent(StatsigEventType.TripOverviewActivityShowDetailsClicked);
  }

  function logTripOverviewActivityEditFiltersClicked(): void {
    logEvent(StatsigEventType.TripOverviewActivityEditFiltersClicked);
  }

  function logTripOverviewFerryEditFiltersClicked(): void {
    logEvent(StatsigEventType.TripOverviewFerryEditFiltersClicked);
  }

  function logTripOverviewFerryChangeAirportsClicked(): void {
    logEvent(StatsigEventType.TripOverviewFerryChangeAirportsClicked);
  }

  function logTripOverviewFlightChangeAirportsClicked(): void {
    logEvent(StatsigEventType.TripOverviewFlightChangeAirportsClicked);
  }

  function logTripOverviewActivityTryAnotherDateClicked(): void {
    logEvent(StatsigEventType.TripOverviewActivityTryAnotherDateClicked);
  }

  function logTripOverviewFerryTryAnotherDateClicked(): void {
    logEvent(StatsigEventType.TripOverviewFerryTryAnotherDateClicked);
  }

  function logTripOverviewShareOrSaveButtonClicked(): void {
    uiInteractionsState.value.shareTripOverview = true;
    logEvent(StatsigEventType.TripOverviewShareOrSaveButtonClicked);
  }

  function logTripOverviewTripSummaryPanelOpened(): void {
    uiInteractionsState.value.tripSummaryOverview = true;
    logEvent(StatsigEventType.TripOverviewTripSummaryPanelOpened);
  }

  function logTripOverviewGlobalSuggestionSwitched(
    globalSuggestionSwitchType: 'cheapest' | 'popular'
  ): void {
    logEvent(StatsigEventType.TripOverviewGlobalSuggestionSwitched, undefined, {
      globalSuggestionSwitchType,
    });
  }

  function logTripOverviewSeeAllHotelsClicked(): void {
    logEvent(StatsigEventType.TripOverviewSeeAllHotelsClicked);
  }

  function logTripOverviewRemoveDestination(): void {
    logEvent(StatsigEventType.TripOverviewRemoveDestination);
  }

  function logTripOverviewConfigurationPanelOpened(
    metaData: TripOverviewConfigurationPanelMetadata
  ): void {
    logEvent(
      StatsigEventType.TripOverviewConfigurationPanelOpened,
      undefined,
      metaData
    );
  }

  function logTripOverviewReloadPageModalOpened(): void {
    logEvent(StatsigEventType.TripOverviewReloadPageModalOpened);
  }

  function logTripOverviewReloadPageButtonClicked(): void {
    logEvent(StatsigEventType.TripOverviewReloadPageButtonClicked);
  }

  function logTripOverviewReloadPageFailed(): void {
    logEvent(StatsigEventType.TripOverviewReloadPageFailed);
  }

  function logTripOverviewReloadPageSuccess(): void {
    logEvent(StatsigEventType.TripOverviewReloadPageSuccess);
  }

  function logTripOverviewAccommodationUpsellHotelAdded(): void {
    logEvent(StatsigEventType.TripOverviewAccommodationUpsellHotelAdded);
  }

  function logTripOverviewScrollToSummary(): void {
    logEvent(StatsigEventType.TripOverviewScrollToSummary);
  }

  function logTripOverviewPayLaterModalOpened(): void {
    logEvent(StatsigEventType.TripOverviewPayLaterModalOpened);
  }

  function logTripOverviewTravellingWithSemboModalOpened(
    isUserLoggedIn: boolean
  ): void {
    uiInteractionsState.value.travellingWithSemboOverview = true;
    logEvent(
      StatsigEventType.TripOverviewTravellingWithSemboModalOpened,
      undefined,
      {
        isUserLoggedIn: isUserLoggedIn.toString(),
      }
    );
  }

  // Actions: Hotel selection
  function logHotelSelectionInitiated(
    metadata: HotelSelectionInitiatedMetadata
  ): void {
    logEvent(StatsigEventType.HotelSelectionInitiated, undefined, {
      placeId: metadata.placeId?.toString() ?? '',
      placeName: metadata.placeName,
      hotelResults: metadata.hotelResults.toString(),
    });
  }

  function logHotelSelectionFilterOpened(
    metadata: HotelSelectionFilterOpenedMetadata
  ): void {
    logEvent(StatsigEventType.HotelSelectionFilterOpened, undefined, {
      polygonId: metadata.polygonId?.toString() ?? '',
      label: metadata.label,
      hotelResults: metadata.hotelResults.toString(),
      source: metadata.source,
    });
  }

  function logHotelSelectionFilterClosed(
    metadata: HotelSelectionFilterClosedMetadata
  ): void {
    logEvent(StatsigEventType.HotelSelectionFilterClosed, undefined, {
      polygonId: metadata.polygonId?.toString() ?? '',
      label: metadata.label,
      hotelResults: metadata.hotelResults.toString(),
      eventPlacement: metadata.eventPlacement,
    });
  }

  function logHotelSelectionFilterApplied(
    metadata: HotelSelectionFilterAppliedMetadata
  ): void {
    logEvent(StatsigEventType.HotelSelectionFilterApplied, undefined, {
      filterName: metadata.filterName,
      filterGroup: metadata.filterGroup,
      filterDetail: metadata.filterDetail,
      polygonId: metadata.polygonId?.toString() ?? '',
      pathname: metadata.pathname,
      eventPlacement: metadata.eventPlacement,
    });
  }

  function logHotelSelectionFilterRemoved(
    metadata: HotelSelectionFilterRemovedMetadata
  ): void {
    logEvent(StatsigEventType.HotelSelectionFilterRemoved, undefined, {
      filterName: metadata.filterName,
      filterGroup: metadata.filterGroup,
      filterDetail: metadata.filterDetail,
      polygonId: metadata.polygonId?.toString() ?? '',
      pathname: metadata.pathname,
      eventPlacement: metadata.eventPlacement,
    });
  }

  function logHotelSelectionFilterReset(
    metadata: HotelSelectionFilterResetMetadata
  ): void {
    logEvent(StatsigEventType.HotelSelectionFilterReset, undefined, {
      polygonId: metadata.polygonId?.toString() ?? '',
      label: metadata.label,
    });
  }

  function logHotelSelectionMapMarkerClicked(
    metadata: HotelSelectionMapEventMetadata
  ): void {
    logEvent(StatsigEventType.HotelSelectionMapMarkerClicked, undefined, {
      ...metadata,
    });
  }

  function logHotelSelectionListCardClicked(
    metadata: HotelSelectionListCardClickedMetaData
  ): void {
    logEvent(StatsigEventType.HotelSelectionListCardClicked, undefined, {
      isPreviouslySeenHotel: metadata.isPreviouslySeenHotel.toString(),
      position: metadata.position.toString(),
      hotelCode: metadata.hotelCode,
    });
  }

  function logHotelSelectionMapCardClicked(
    metadata: HotelSelectionMapEventMetadata
  ): void {
    logEvent(StatsigEventType.HotelSelectionMapCardClicked, undefined, {
      ...metadata,
    });
  }

  function logHotelSelectionBackButtonClicked(): void {
    logEvent(StatsigEventType.HotelSelectionBackButtonClicked);
  }

  function logHotelSelectionPeekPriceModalOpened(
    metadata: HotelPeekPriceModalOpenedMetadata
  ): void {
    uiInteractionsState.value.peekPriceCalendarHotelSelection = true;
    logEvent(StatsigEventType.HotelSelectionPeekPriceModalOpened, undefined, {
      ...metadata,
    });
  }

  function logHotelSelectionPeekPriceModalDateSelected(): void {
    logEvent(StatsigEventType.HotelSelectionPeekPriceModalDateSelected);
  }

  function logHotelSelectionPeekPriceModalMonthSelected(): void {
    logEvent(StatsigEventType.HotelSelectionPeekPriceModalMonthSelected);
  }

  function logHotelSelectionExtendMap(): void {
    logEvent(StatsigEventType.HotelSelectionExtendMap);
  }

  function logHotelSelectionCollapseMap(): void {
    logEvent(StatsigEventType.HotelSelectionCollapseMap);
  }

  function logHotelSelectionToggleView(
    metadata: HotelSelectionToggleViewMetadata
  ): void {
    logEvent(StatsigEventType.HotelSelectionToggleView, undefined, {
      ...metadata,
    });
  }

  function logHotelSelectionToggleMapType(
    metadata: ToggleMapTypeMetadata
  ): void {
    logEvent(StatsigEventType.HotelSelectionToggleMapType, undefined, {
      ...metadata,
    });
  }

  function logHotelSelectionNoResultsResetClicked(): void {
    logEvent(StatsigEventType.HotelSelectionNoResultsResetClicked);
  }

  function logHotelSelectionSearchThisAreaClicked(): void {
    logEvent(StatsigEventType.HotelSelectionSearchThisAreaClicked);
  }

  function logHotelSelectionMapClusterClicked(): void {
    logEvent(StatsigEventType.HotelSelectionMapClusterClicked);
  }

  function logHotelSelectionUpsellBannerDisplayed(
    metadata: HotelSelectionUpsellBannerDisplayedMetadata
  ): void {
    logEvent(StatsigEventType.HotelSelectionUpsellBannerDisplayed, undefined, {
      bannerType: metadata.bannerType,
      fromIata: metadata.fromIata,
      toIata: metadata.toIata,
    });
  }

  function logHotelSelectionUpsellBannerToggled(
    metadata: HotelSelectionUpsellBannerToggledMetadata
  ): void {
    logEvent(StatsigEventType.HotelSelectionUpsellBannerToggled, undefined, {
      expanded: metadata.expanded.toString(),
    });
  }

  function logHotelSelectionSuggestionTypeSelected(
    metadata: HotelSelectionSuggestionTypeSelectedMetadata
  ): void {
    logEvent(StatsigEventType.HotelSelectionSuggestionTypeSelected, undefined, {
      ...metadata,
    });
  }

  // Actions: Hotel details
  function logHotelDetailsInitiated(metadata: HotelDetailsEventMetadata): void {
    logEvent(StatsigEventType.HotelDetailsInitiated, undefined, {
      ...metadata,
    });
  }

  function logHotelDetailsSelected(metadata: HotelDetailsEventMetadata): void {
    logEvent(StatsigEventType.HotelDetailsSelected, undefined, { ...metadata });
  }

  function logHotelDetailsDeselected(
    metadata: HotelDetailsEventMetadata
  ): void {
    logEvent(StatsigEventType.HotelDetailsDeselected, undefined, {
      ...metadata,
    });
  }

  function logHotelDetailsStaySelected(
    metadata: HotelDetailsEventMetadata
  ): void {
    logEvent(StatsigEventType.HotelDetailsStaySelected, undefined, {
      ...metadata,
    });
  }

  function logHotelDetailsShowStaysButtonClicked(
    metadata: HotelDetailsEventMetadata
  ): void {
    logEvent(StatsigEventType.HotelDetailsShowStaysButtonClicked, undefined, {
      ...metadata,
    });
  }

  function logHotelDetailsContinueWithSelectedRooms(
    metadata: HotelDetailsEventMetadata
  ): void {
    logEvent(
      StatsigEventType.HotelDetailsContinueWithSelectedRooms,
      undefined,
      {
        ...metadata,
      }
    );
  }

  // Actions: Activity Selection
  function logActivitySelectionInitiated(): void {
    logEvent(StatsigEventType.ActivitySelectionInitiated);
  }

  function logActivitySelectionGalleryImageSwipe(): void {
    logEvent(StatsigEventType.ActivitySelectionGalleryImageSwipe);
  }

  function logActivitySelectionFilterOpened(): void {
    logEvent(StatsigEventType.ActivitySelectionFilterOpened);
  }

  function logActivitySelectionMapMarkerClicked(
    metadata: ActivitySelectionMapEventMetadata
  ): void {
    logEvent(StatsigEventType.ActivitySelectionMapMarkerClicked, undefined, {
      ...metadata,
    });
  }

  function logActivitySelectionListCardClicked(
    metadata: ActivitySelectionListCardClickedMetaData
  ): void {
    logEvent(StatsigEventType.ActivitySelectionListCardClicked, undefined, {
      ...metadata,
      position: metadata.position.toString(),
    });
  }

  function logActivitySelectionMapCardClicked(
    metadata: ActivitySelectionMapEventMetadata
  ): void {
    logEvent(StatsigEventType.ActivitySelectionMapCardClicked, undefined, {
      ...metadata,
    });
  }

  // Actions: Activity details
  function logActivityDetailsInitiated(): void {
    logEvent(StatsigEventType.ActivityDetailsInitiated);
  }

  function logActivityDetailsTicketSelected(
    metadata: ActivityDetailsTicketSelectedMetadata
  ): void {
    logEvent(StatsigEventType.ActivityDetailsTicketSelected, undefined, {
      productCode: metadata.productCode,
    });
  }

  function logActivityDetailsOpenTicketsSelector(): void {
    logEvent(StatsigEventType.ActivityDetailsOpenTicketsSelector);
  }

  // Actions: Flights List
  function logFlightsListInitiated(): void {
    logEvent(StatsigEventType.FlightListInitiated);
  }

  function logFlightsListProductSelected(): void {
    logEvent(StatsigEventType.FlightListProductSelected);
  }

  function logFlightsListProductDeselected(): void {
    logEvent(StatsigEventType.FlightListProductDeselected);
  }

  function logFlightsListFilterModalOpened(): void {
    logEvent(StatsigEventType.FlightListFilterModalOpened);
  }

  function logFlightsListFilterApplied(): void {
    logEvent(StatsigEventType.FlightListFilterApplied);
  }

  function logFlightsListViewTripClicked(): void {
    logEvent(StatsigEventType.FlightListViewTripClicked);
  }

  function logFlightsListEditModalOpened(): void {
    logEvent(StatsigEventType.FlightListEditModalOpened);
  }

  function logFlightsListNightsChanged(): void {
    logEvent(StatsigEventType.FlightListNightsChanged);
  }

  // Actions: Ferry List
  function logFerryListInitiated(): void {
    logEvent(StatsigEventType.FerryListInitiated);
  }

  function logFerryListProductSelected(): void {
    logEvent(StatsigEventType.FerryListProductSelected);
  }

  // Actions: External errors
  function logExternalErrorDisplayed(
    externalErrorType: ExternalErrorQueryParamValue
  ): void {
    logEvent(StatsigEventType.ExternalErrorDisplayed, undefined, {
      externalErrorType,
      url: window.location.href,
    });
  }

  function logBookingFailedErrorTryAgain(): void {
    logEvent(StatsigEventType.BookingFailedErrorTryAgain);
  }

  function logBookingFailedErrorBuildNewTrip(): void {
    logEvent(StatsigEventType.BookingFailedErrorBuildNewTrip);
  }

  function logTransportStaticInfoNotAvailable(missingCarrier: string): void {
    logEvent(StatsigEventType.TransportStaticInfoNotAvailable, missingCarrier);
  }

  // Actions: Telemetry
  function logMissingAmenityConfiguration(unknownAmenityName: string): void {
    logEvent(StatsigEventType.MissingAmenityConfiguration, unknownAmenityName);
  }

  function logTripOverviewInfiniteLoad(): void {
    logEvent(StatsigEventType.TripOverviewInfiniteLoad, undefined, {
      ...getUrlMetadata(),
    });
  }

  function logTripOverviewInfiniteLoadReasons(
    itineraryError:
      | MissingItineraryAvailabilityError
      | MissingItineraryStaticInfoError
  ): void {
    if (itineraryError instanceof MissingItineraryAvailabilityError) {
      logEvent(
        StatsigEventType.TripOverviewInfiniteLoadMissingAvailability,
        undefined,
        {
          ...getUrlMetadata(),
        }
      );
    } else {
      itineraryError.ingredientErrors.forEach((error) => {
        if (error instanceof MissingOwnStayStaticInfoError) {
          logEvent(
            StatsigEventType.TripOverviewInfiniteLoadMissingStaticInfo,
            undefined,
            {
              ingredientType: IngredientType.OwnStay,
              ...getUrlMetadata(),
            }
          );
        } else if (error instanceof MissingHotelStaticInfoError) {
          logEvent(
            StatsigEventType.TripOverviewInfiniteLoadMissingStaticInfo,
            undefined,
            {
              ingredientType: IngredientType.Hotel,
              date: error.date.toISOString(),
              code: error.code ?? '',
              availability: error.availability ?? '',
              ...getUrlMetadata(),
            }
          );
        } else if (error instanceof MissingTransportStaticInfoError) {
          logEvent(
            StatsigEventType.TripOverviewInfiniteLoadMissingStaticInfo,
            undefined,
            {
              ingredientType: IngredientType.Transport,
              transportType: error.transportType,
              date: error.date.toISOString(),
              availability: error.availability,
              transportIds: error.transportIds.join(','),
              missingPorts: error.missingPorts.join(','),
              missingCarriers: error.missingCarriers.join(','),
              ...getUrlMetadata(),
            }
          );
        } else if (error instanceof MissingActivitiesStaticInfoError) {
          error.activitiesWithMissingStaticInfo.forEach((activityError) => {
            logEvent(
              StatsigEventType.TripOverviewInfiniteLoadMissingStaticInfo,
              undefined,
              {
                ingredientType: IngredientType.Activity,
                availability: activityError.availability,
                code: activityError.code,
                date: activityError.date.toISOString(),
                ...getUrlMetadata(),
              }
            );
          });
        } else if (error instanceof MissingOwnTransportStaticInfoError) {
          logEvent(
            StatsigEventType.TripOverviewInfiniteLoadMissingStaticInfo,
            undefined,
            {
              ingredientType: IngredientType.OwnTransport,
              ...getUrlMetadata(),
            }
          );
        }
      });
    }
  }

  function logHotelSelectionInfiniteLoad(): void {
    logEvent(StatsigEventType.HotelSelectionInfiniteLoad, undefined, {
      ...getUrlMetadata(),
    });
  }

  function logMetaHotelListEmpty(metadata: MetaHotelListEmptyMetadata): void {
    logEvent(StatsigEventType.MetaHotelListEmpty, undefined, {
      ...metadata,
      ...getUrlMetadata(),
    });
  }

  function logMissingCarrierLogo(metadata: MissingCarrierLogoMetadata): void {
    logEvent(StatsigEventType.MissingCarrierLogo, undefined, {
      ...metadata,
    });
  }

  function logHotelSelectionWithWrongIndexAccessed(currentUrl: string): void {
    logEvent(StatsigEventType.HotelSelectionWithWrongIndexAccessed, undefined, {
      currentUrl,
    });
  }

  function logHotelSelectionWithOldIndexAccessed(currentUrl: string): void {
    logEvent(StatsigEventType.HotelSelectionWithOldIndexAccessed, undefined, {
      currentUrl,
    });
  }

  function logActivitySelectionWithWrongIndexAccessed(): void {
    logEvent(StatsigEventType.ActivitySelectionWithWrongIndexAccessed);
  }

  function logActivitySelectionWithOldIndexAccessed(): void {
    logEvent(StatsigEventType.ActivitySelectionWithOldIndexAccessed);
  }

  function logEvent(
    eventName: string,
    eventValue?: string | number,
    eventMetadata?: Record<string, string>
  ): void {
    logStatsigEventWrapper(
      eventName,
      eventValue,
      eventMetadata,
      baseMetadata.value
    );
  }

  function logCarPlannerInitiated(): void {
    logEvent(StatsigEventType.CarPlannerInitialized);
  }

  function logCarPlannerShowSuggestedTrip(): void {
    logEvent(StatsigEventType.CarPlannerShowSuggestedTrip);
  }

  // Action: Print mode
  function logTripOverviewPrintModeOpened(
    metadata: TripOverviewPrintModeMetadata
  ): void {
    logEvent(StatsigEventType.TripOverviewPrintModeOpened, undefined, {
      source: metadata.source,
    });
  }

  return {
    // Getters
    baseMetadata,

    // Actions: Core
    logCoreDateChanged,
    logCorePaxModalOpened,
    logCorePaxUpdated,
    logNoAvailForMonth,
    logCoreNightsSelectorOpened,
    logCoreDateSelectedAtLeastOneYearAhead,
    logCoreTripAutoSavedAttempt,

    // Actions: Build your trip
    logBuildYourTripInitiated,

    // Actions: NOTE: Helper events for experiments, remove when not needed anymore
    logTripOverviewMobileMainSummaryActionClicked,

    // Actions: Trip overview
    logTripOverviewInitiated,
    logTripOverviewMobileControlCenterToggled,
    logTripOverviewAccommodationSelectorOpened,
    logTripOverviewAddDestinationClicked,
    logTripOverviewBookClicked,
    logTripOverviewBookClickedWithFarAirport,
    logTripOverviewWarningModalDisplayed,
    logTripOverviewWarningModalClosed,
    logTripOverviewWarningModalContinued,
    logTripOverviewCheckoutRedirect,
    logOverviewCheckoutBasketError,
    logOverviewCheckoutTokenError,
    logOverviewCheckoutUnknownError,
    logTripOverviewHotelChangeClicked,
    logTripOverviewTransportSelectorOpened,
    logTripOverviewExploreActivitiesClicked,
    logTripOverviewActivityBannerElementClicked,
    logTripOverviewHotelToggle,
    logTripOverviewEditTripModalOpened,
    logTripOverviewEditTripModalDestinationAdded,
    logTripOverviewEditTripModalDestinationChanged,
    logTripOverviewEditTripModalDestinationRearranged,
    logTripOverviewFlyFromButtonClicked,
    logTripOverviewUnavailableProductActionClicked,
    logTripOverviewUnavailableProductRendered,
    logTripOverviewAlternativeStaySelected,
    logTripOverviewAlternativeActivitySelected,
    logTripOverviewSeeAlternativeRoomsWhenNoAvail,
    logTripOverviewChangeDateClicked,
    logTripOverviewConfirmAlternativeRoomSuggestionWhenNoAvail,
    logTripOverviewSeeAlternativeTicketsWhenNoAvail,
    logTripOverviewAlternativeRoomsDisplayed,
    logTripOverviewAlternativeTicketsDisplayed,
    logTripOverviewAlternativeFlightDisplayed,
    logTripOverviewAlternativeFlightSelected,
    logTripOverviewAlternativeFerryDisplayed,
    logTripOverviewAlternativeFerrySelected,
    logTripOverviewHeaderActionClicked,
    logTripOverviewAlternativeAccommodationGalleryImageSwipe,
    logTripOverviewActivityGalleryImageSwipe,
    logTripOverviewAlternativeActivityGalleryImageSwipe,
    logTripOverviewContinueWithSelectedDateModalOpened,
    logTripOverviewEditRoomClicked,
    logTripOverviewActivityRemoved,
    logTripOverviewFlightAdded,
    logTripOverviewFlightRemoved,
    logTripOverviewFerryAdded,
    logTripOverviewFerryRemoved,
    logTripOverviewTransportTicketTypeSelected,
    logTripOverviewFerryTicketUpsellSamePriceHidden,
    logTripOverviewFlightChangeClicked,
    logTripOverviewFerryChangeClicked,
    logTripOverviewActivityChangeClicked,
    logTripOverviewFlightShowDetailsClicked,
    logTripOverviewFerryShowDetailsClicked,
    logTripOverviewHotelShowDetailsClicked,
    logTripOverviewActivityShowDetailsClicked,
    logTripOverviewActivityEditFiltersClicked,
    logTripOverviewFerryEditFiltersClicked,
    logTripOverviewActivityTryAnotherDateClicked,
    logTripOverviewFerryTryAnotherDateClicked,
    logTripOverviewFerryChangeAirportsClicked,
    logTripOverviewFlightChangeAirportsClicked,
    logTripOverviewTripSummaryPanelOpened,
    logTripOverviewShareOrSaveButtonClicked,
    logTripOverviewGlobalSuggestionSwitched,
    logTripOverviewSeeAllHotelsClicked,
    logTripOverviewRemoveDestination,
    logTripOverviewConfigurationPanelOpened,
    logTripOverviewReloadPageModalOpened,
    logTripOverviewReloadPageButtonClicked,
    logTripOverviewReloadPageFailed,
    logTripOverviewReloadPageSuccess,
    logTripOverviewAccommodationUpsellHotelAdded,
    logTripOverviewScrollToSummary,
    logTripOverviewPayLaterModalOpened,
    logTripOverviewTravellingWithSemboModalOpened,

    // Actions: Hotel selection
    logHotelSelectionInitiated,
    logHotelSelectionFilterOpened,
    logHotelSelectionFilterClosed,
    logHotelSelectionFilterApplied,
    logHotelSelectionFilterRemoved,
    logHotelSelectionFilterReset,
    logHotelSelectionMapMarkerClicked,
    logHotelSelectionListCardClicked,
    logHotelSelectionMapCardClicked,
    logHotelSelectionBackButtonClicked,
    logHotelSelectionPeekPriceModalOpened,
    logHotelSelectionPeekPriceModalDateSelected,
    logHotelSelectionPeekPriceModalMonthSelected,
    logHotelSelectionExtendMap,
    logHotelSelectionCollapseMap,
    logHotelSelectionToggleView,
    logHotelSelectionToggleMapType,
    logHotelSelectionNoResultsResetClicked,
    logHotelSelectionSearchThisAreaClicked,
    logHotelSelectionMapClusterClicked,
    logHotelSelectionUpsellBannerDisplayed,
    logHotelSelectionUpsellBannerToggled,
    logHotelSelectionSuggestionTypeSelected,

    // Actions: Hotel details
    logHotelDetailsInitiated,
    logHotelDetailsSelected,
    logHotelDetailsDeselected,
    logHotelDetailsStaySelected,
    logHotelDetailsShowStaysButtonClicked,
    logHotelDetailsContinueWithSelectedRooms,

    // Actions: Activity Selection
    logActivitySelectionInitiated,
    logActivitySelectionGalleryImageSwipe,
    logActivitySelectionFilterOpened,
    logActivitySelectionMapMarkerClicked,
    logActivitySelectionListCardClicked,
    logActivitySelectionMapCardClicked,

    // Actions: Activity details
    logActivityDetailsInitiated,
    logActivityDetailsTicketSelected,
    logActivityDetailsOpenTicketsSelector,

    // Actions: Flights List
    logFlightsListInitiated,
    logFlightsListProductSelected,
    logFlightsListProductDeselected,
    logFlightsListFilterModalOpened,
    logFlightsListFilterApplied,
    logFlightsListViewTripClicked,
    logFlightsListEditModalOpened,
    logFlightsListNightsChanged,

    // Actions: Ferry List
    logFerryListInitiated,
    logFerryListProductSelected,

    // Actions: External errors
    logExternalErrorDisplayed,
    logBookingFailedErrorTryAgain,
    logBookingFailedErrorBuildNewTrip,
    logTransportStaticInfoNotAvailable,

    // Actions: Telemetry
    logMissingAmenityConfiguration,
    logTripOverviewInfiniteLoad,
    logTripOverviewInfiniteLoadReasons,
    logHotelSelectionInfiniteLoad,
    logMetaHotelListEmpty,
    logMissingCarrierLogo,
    logHotelSelectionWithWrongIndexAccessed,
    logHotelSelectionWithOldIndexAccessed,
    logActivitySelectionWithWrongIndexAccessed,
    logActivitySelectionWithOldIndexAccessed,

    // Actions: Car planner
    logCarPlannerInitiated,
    logCarPlannerShowSuggestedTrip,

    // Actions: Print mode
    logTripOverviewPrintModeOpened,
  };
});
